import { computed, effect, inject, untracked } from '@angular/core';
import { patchState, signalStoreFeature, type, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { AdvancedCommerceStore } from '../advanced-commerce-store.namespace';
import { AdvancedCommerceStoreService, injectRouteParams } from '../advanced-commerce-store.service';

export function withMeta() {
    return signalStoreFeature(
        {
            state: type<AdvancedCommerceStore.State & AdvancedCommerceStore.Response.State>()
        },
        withState<AdvancedCommerceStore.Meta.State>({
            metaAdvertCount: 0,
            metaClickCode: '',
            metaCount: 0,
            metaCurrentPage: 1,
            metaIsPagingRequest: false,
            metaFrom: 1,
            metaPageCount: 1,
            metaPageSize: 24,
            metaProductCount: 0,
            metaTo: 24
        }),
        withComputed(store => {
            const routeParams = injectRouteParams();

            return {
                queryParamCurrentPage: computed<string>(() => routeParams()['pg']),
                /**
                 * Whether the store has more items to load
                 */
                hasMore: computed(() => store.metaCurrentPage() < store.metaPageCount())
            };
        }),
        withMethods(store => {
            return {
                updateMeta(meta: AdvancedCommerceStore.Meta.State) {
                    patchState(store, {
                        metaAdvertCount: meta.metaAdvertCount,
                        metaClickCode: meta.metaClickCode,
                        metaCount: meta.metaCount,
                        metaCurrentPage: meta.metaCurrentPage,
                        metaFrom: meta.metaFrom,
                        metaPageCount: meta.metaPageCount,
                        metaPageSize: meta.metaPageSize,
                        metaProductCount: meta.metaProductCount,
                        metaTo: meta.metaTo
                    });
                },
                metaIsPagingRequestSet(value: boolean) {
                    patchState(store, state => ({ ...state, metaIsPagingRequest: value }));
                },
                loadMore() {
                    const currentPage = store.metaCurrentPage() + 1;

                    patchState(store, state => ({ ...state, metaIsPagingRequest: true, metaCurrentPage: currentPage }));
                }
            };
        }),
        withHooks(store => {
            const advancedCommerceStoreService = inject(AdvancedCommerceStoreService);

            return {
                onInit() {
                    /**
                     * Update meta data on request load
                     */
                    effect(() => {
                        const response = store.response();
                        const element = store.element();

                        if (response && element) {
                            const meta = advancedCommerceStoreService.getMeta('products', response, element);

                            untracked(() => {
                                store.updateMeta(meta);
                            });
                        }
                    });

                    /**
                     * Watch for update metaCurrentPage and update query param `pg`
                     */
                    effect(() => {
                        const metaCurrentPage = store.metaCurrentPage();

                        untracked(() => {
                            const response = store.response();
                            const element = store.element();
                            const currentPage = advancedCommerceStoreService.getCurrentPage(
                                'products',
                                response,
                                element,
                                store.queryParamCurrentPage()
                            );

                            if (metaCurrentPage > 1 && currentPage !== metaCurrentPage.toString()) {
                                advancedCommerceStoreService.setCurrentPage(metaCurrentPage);
                            } else {
                                // reset paging request state
                                store.metaIsPagingRequestSet(false);
                            }
                        });
                    });
                }
            };
        })
    );
}
